import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/services/api';
import { Course } from '@/types/course';
import { CourseFormValues } from '@/pages/admin/course-editor/CourseForm';
import { parseLabelValue } from '@/utils/labelUtils';

export interface LessonData {
  id: string;
  title: string;
  orderIndex: number;
  content?: string;
  videoUrl?: string;
  isCompleted?: boolean;
  completedAt?: string;
}

// Extended course type to include labels
interface CourseWithLabels extends Course {
  labels?: string[];
}

export const useCourseEditor = (courseId: string | undefined, userId: string | undefined) => {
  const navigate = useNavigate();
  const isNewCourse = courseId === 'new';
  
  const [initialFormValues, setInitialFormValues] = useState<CourseFormValues>({
    title: '',
    description: '',
    coverImage: '',
    published: false,
    estimatedTime: '',
    labels: [],
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lessons, setLessons] = useState<LessonData[]>([]);
  
  useEffect(() => {
    if (isNewCourse) {
      // Set default values for a new course
      setIsLoading(false);
    } else if (courseId) {
      loadCourseData();
    }
  }, [courseId, isNewCourse]);
  
  // Load course data from API
  const loadCourseData = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/api/course/${courseId}`);
      const courseData = response.data as CourseWithLabels;
      
      if (courseData) {
        // Update form with existing course data
        setInitialFormValues({
          title: courseData.title,
          description: courseData.description || '',
          // Use imageUrl for coverImage field
          coverImage: courseData.imageUrl || '',
          // Convert status string to published boolean
          published: courseData.status === 'published',
          // Use estimatedTime directly
          estimatedTime: courseData.estimatedTime || '',
          labels: parseLabelValue(courseData.labels),
        });
        
        // Set lessons data if available
        if (courseData.lessons) {
          setLessons(courseData.lessons.map((lesson: any) => ({
            id: lesson.id,
            title: lesson.title,
            orderIndex: lesson.orderIndex,
            content: lesson.content,
            videoUrl: lesson.videoUrl,
            isCompleted: lesson.isCompleted,
            completedAt: lesson.completedAt,
          })));
        }
      }
    } catch (error) {
      console.error('Failed to load course:', error);
      toast.error('Could not load course data');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle course creation or update
  const handleSubmit = async (values: CourseFormValues) => {
    setIsSaving(true);
    
    try {
      // Create a course data object with the correct field structure matching the server API
      const courseData = {
        ...(courseId && courseId !== 'new' ? { id: courseId } : {}),
        title: values.title,
        description: values.description,
        estimatedTime: values.estimatedTime || null,
        // Convert published boolean to a status string
        status: values.published ? 'published' : 'draft',
        // Use coverImage for imageUrl
        imageUrl: values.coverImage || null,
        // Include labels - ensure it's an array by parsing if necessary
        labels: parseLabelValue(values.labels),
      };
      
      if (isNewCourse) {
        // Create a new course
        try {
          // Create the course using our API service
          const response = await api.post('/api/course', courseData);
          const newCourse = response.data as CourseWithLabels;
          
          if (newCourse) {
            toast.success('Course created successfully');
            
            // Navigate to the edit page for the new course
            navigate(`/admin/course/${newCourse.id}`);
            
            // Update form values with the returned data
            setInitialFormValues({
              title: newCourse.title,
              description: newCourse.description || '',
              // Use imageUrl as coverImage
              coverImage: newCourse.imageUrl || '',
              // Map status string back to published boolean
              published: newCourse.status === 'published',
              // Use estimatedTime directly
              estimatedTime: newCourse.estimatedTime || '',
              labels: parseLabelValue(newCourse.labels),
            });
          } else {
            throw new Error('No data returned after course creation');
          }
        } catch (createError) {
          console.error('Failed to create course:', createError);
          toast.error('Could not create course: ' + (createError instanceof Error ? createError.message : 'Unknown error'));
        }
      } else if (courseId && courseId !== 'new') {
        // Update existing course
        try {
          // Update the course using our API service
          const response = await api.put(`/api/course/${courseId}`, courseData);
          const updatedCourse = response.data as CourseWithLabels;
          
          if (updatedCourse) {
            toast.success('Course updated successfully');
            
            // Update form values with the returned data
            setInitialFormValues({
              title: updatedCourse.title,
              description: updatedCourse.description || '',
              // Use imageUrl as coverImage
              coverImage: updatedCourse.imageUrl || '',
              // Map status string back to published boolean
              published: updatedCourse.status === 'published',
              // Use estimatedTime directly
              estimatedTime: updatedCourse.estimatedTime || '',
              labels: parseLabelValue(updatedCourse.labels),
            });
          } else {
            toast.error('Failed to update course - no data returned');
          }
        } catch (updateError) {
          console.error('Failed to update course:', updateError);
          toast.error('Could not update course: ' + (updateError instanceof Error ? updateError.message : 'Unknown error'));
        }
      } else {
        console.error('Invalid course state - courseId:', courseId);
        toast.error('Invalid course state. Please refresh the page and try again.');
      }
    } catch (error) {
      console.error('Error handling course submit:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  return {
    isLoading,
    isSaving,
    isNewCourse,
    initialFormValues,
    lessons,
    setLessons,
    handleSubmit,
  };
};
